// A face's whole character (PLAN-2026-10-24-faces-round-three §2.2): the same
// head `FaceArt` draws, on a small body in its outfit, with the number on its
// jersey and something in its hand. A toy's proportions, a big head on a
// short body, as the face's are.
//
// ONE BOX FOR BOTH. The head is drawn in its own 100 × 100 box at the top, so
// everything that hangs at its neck (a tie, a medal) lands on the collar; the
// body is drawn below in the same units, down to the shoes at about 156.
//
// THE POSE IS THE MOOD (§2.1): at rest the arms hang, pleased they cheer,
// annoyed the hands go on the hips, thinking a hand goes to the chin, talking
// an arm gestures. The face's mood is still the stylesheet's (`data-state`,
// kit.css); the arms are drawn for it, and the cheer hops unless motion is
// reduced.
//
// TWO FACING EACH OTHER (PLAN-2026-10-31-a-handshake-a-dance-and-the-photo
// §1): the second is drawn with its pose the other way round (`mirror`), not
// flipped, so the number on its jersey still reads.
//
// EVERY PAINTED REGION NAMES ITS AREA (`data-slot`), as the face's do, so the
// paint can fills a jersey as it fills a hat. The number is paths, not text,
// so it looks the same in every browser and in a saved picture.

import type { ReactNode } from 'react';
import {
  NO_TEAM,
  NUMBERED_OUTFITS,
  darken,
  faceOutfit,
  type Face,
  type FaceArea,
  type FaceHeld,
  type FaceMood,
  type FaceOutfit,
  type FigureTeam,
} from '@fsp/domain';
import {
  Both,
  FaceDrawing,
  INK,
  THROUGH,
  faceCtx,
  heart,
  paint,
  star,
  useDrawingId,
  type Ctx,
} from './FaceArt';

/** The character's box: the head's 100 × 100 at the top, with room for raised arms and a stage. */
export const FIGURE_BOX = { x: -12, y: -8, w: 124, h: 172 } as const;
const BOX = `${FIGURE_BOX.x} ${FIGURE_BOX.y} ${FIGURE_BOX.w} ${FIGURE_BOX.h}`;

/**
 * Where a part of the character is in its box, for the maker's grid, which
 * shows each outfit and each thing to hold on your own character, cropped.
 */
export const FIGURE_PART_VIEW: Partial<Record<string, string>> = {
  body: '14 80 72 80',
  held: '-6 40 112 120',
};

type Point = readonly [number, number];
interface Arm {
  /** The elbow and the hand. */
  readonly e: Point;
  readonly h: Point;
}
interface Pose {
  /** The left arm as you look at the character, and the right, which holds things. */
  readonly l: Arm;
  readonly r: Arm;
  /** Arms raised behind the head rather than in front of the body. */
  readonly behind: boolean;
}

const SHOULDER_L: Point = [34, 96];
const SHOULDER_R: Point = [66, 96];
const AT_REST_R: Arm = { e: [72, 112], h: [73, 125] };

/** Each mood's pose (§2.1). */
const POSES: Record<FaceMood, Pose> = {
  idle: { l: { e: [28, 112], h: [27, 125] }, r: AT_REST_R, behind: false },
  pleased: { l: { e: [23, 86], h: [17, 71] }, r: { e: [77, 86], h: [83, 71] }, behind: true },
  annoyed: { l: { e: [21, 111], h: [32, 125] }, r: { e: [79, 111], h: [68, 125] }, behind: false },
  thinking: { l: { e: [29, 117], h: [40, 86] }, r: AT_REST_R, behind: false },
  talking: { l: { e: [21, 106], h: [11, 96] }, r: AT_REST_R, behind: false },
  // The stickers' (PLAN-2026-10-30-stickers §2): hands up and out, taken
  // aback; arms down, asleep; one hand up beside the head, waving hello.
  surprised: { l: { e: [22, 104], h: [13, 92] }, r: { e: [78, 104], h: [87, 92] }, behind: false },
  asleep: { l: { e: [29, 113], h: [29, 126] }, r: { e: [71, 113], h: [71, 126] }, behind: false },
  waving: { l: { e: [20, 95], h: [14, 77] }, r: AT_REST_R, behind: false },
  // Where things happen (PLAN-2026-10-31-a-handshake-a-dance-and-the-photo):
  // the right hand held out level to shake, and one arm up and one out to
  // dance.
  shaking: { l: { e: [28, 112], h: [27, 125] }, r: { e: [83, 101], h: [96, 103] }, behind: false },
  dancing: { l: { e: [20, 93], h: [13, 76] }, r: { e: [82, 103], h: [95, 97] }, behind: false },
};

/** A pose the other way round: each arm where the other one would be, seen in a mirror. */
function mirrored(pose: Pose): Pose {
  const flip = (arm: Arm): Arm => ({
    e: [100 - arm.e[0], arm.e[1]],
    h: [100 - arm.h[0], arm.h[1]],
  });
  return { l: flip(pose.r), r: flip(pose.l), behind: pose.behind };
}

// ── The outfits ────────────────────────────────────────────────────────────

/** How an outfit is drawn on the body. */
interface OutfitLook {
  readonly sleeves: 'long' | 'short' | 'none';
  /** What the sleeves are: the outfit's area unless it is worn over something (overalls, a vest). */
  readonly sleeve?: FaceArea | `#${string}`;
  /** A band of the trim where a sleeve ends (a jersey's), or a stripe down it (a tracksuit's). */
  readonly cuff?: 'band' | 'stripe';
  readonly legs: 'pants' | 'shorts' | 'skirt';
  /** The legs in the pants' color, or the outfit's own for one that is one piece. */
  readonly legsFrom: 'pants' | 'shirt';
  /** The torso and what is on it, under the head. */
  readonly draw: (ctx: Ctx) => ReactNode;
  /** Drawn behind the head and the torso: a hood's back, a cape's. */
  readonly behind?: (ctx: Ctx) => ReactNode;
}

const shirt = (ctx: Ctx, shade?: number) => paint(ctx.c, 'shirt', shade);
const trim = (ctx: Ctx, shade?: number) => paint(ctx.c, 'shirtTrim', shade);

/** A torso, shoulders to waist. */
const TORSO =
  'M35 89.5 Q50 86.5 65 89.5 Q69.5 90.5 69.5 95 L68 131 Q50 134 32 131 L30.5 95 Q30.5 90.5 35 89.5 Z';
const torso = (ctx: Ctx) => <path d={TORSO} {...shirt(ctx)} />;
/** A line in the trim's color, painted as the trim. */
const trimLine = (ctx: Ctx, d: string, width = 2) => (
  <path
    d={d}
    stroke={ctx.c.shirtTrim}
    strokeWidth={width}
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    data-slot="shirtTrim"
  />
);
const crew = (ctx: Ctx) => trimLine(ctx, 'M43 89.2 Q50 94 57 89.2', 2.2);
/** Stripes in the trim across a region, clipped to the torso. */
function clipped(ctx: Ctx, name: string, children: ReactNode) {
  const id = `${ctx.uid}-${name}`;
  return (
    <>
      <clipPath id={id}>
        <path d={TORSO} />
      </clipPath>
      <g clipPath={`url(#${id})`}>{children}</g>
    </>
  );
}

const OUTFITS: Record<FaceOutfit, OutfitLook> = {
  jersey: {
    sleeves: 'short',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {trimLine(ctx, 'M43 89 L50 97 L57 89', 2.4)}
      </>
    ),
  },
  tee: {
    sleeves: 'short',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {crew(ctx)}
      </>
    ),
  },
  'heart-tee': {
    sleeves: 'short',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {crew(ctx)}
        <path d={heart(50, 110, 18)} {...trim(ctx)} />
      </>
    ),
  },
  tank: {
    sleeves: 'none',
    legs: 'shorts',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        <path d="M38 90 Q50 96 62 90 L66 98 L68 131 Q50 134 32 131 L34 98 Z" {...shirt(ctx)} />
        {trimLine(ctx, 'M38 90 Q50 96 62 90', 1.8)}
        {trimLine(ctx, 'M38 90 Q36 95 34 98 M62 90 Q64 95 66 98', 1.6)}
      </>
    ),
  },
  hoodie: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'pants',
    behind: (ctx) => <path d="M33 92 Q31 78 50 79 Q69 78 67 92 Z" {...shirt(ctx, 0.82)} />,
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <path d="M38 116 L62 116 L59 128 L41 128 Z" {...shirt(ctx, 0.88)} />
        {trimLine(ctx, 'M47 93 L46 104 M53 93 L54 104', 1.5)}
      </>
    ),
  },
  collar: {
    sleeves: 'long',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <Both
          d="M42 87.5 L50 95 L43.5 99 Z"
          {...shirt(ctx, 0.92)}
          stroke={darken(ctx.c.shirt, 0.75)}
          strokeWidth={0.6}
        />
        <g {...trim(ctx)}>
          {[103, 111, 119, 127].map((y) => (
            <circle key={y} cx={50} cy={y} r={1.1} />
          ))}
        </g>
      </>
    ),
  },
  turtleneck: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <rect x={41} y={83} width={18} height={10} rx={4} {...shirt(ctx, 0.9)} />
        <path
          d="M44 84 L44 92 M47.5 84 L47.5 92 M50.5 84 L50.5 92 M54 84 L54 92"
          stroke={darken(ctx.c.shirt, 0.72)}
          strokeWidth={0.6}
          {...THROUGH}
        />
      </>
    ),
  },
  overalls: {
    sleeves: 'short',
    sleeve: 'shirtTrim',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        <path d={TORSO} {...trim(ctx)} />
        <path d="M37 105 L63 105 L67 131 Q50 134 33 131 Z" {...shirt(ctx)} />
        <Both
          d="M38.5 106 L36 91"
          stroke={ctx.c.shirt}
          strokeWidth={3.6}
          strokeLinecap="round"
          data-slot="shirt"
        />
        <g fill="#f2c230" stroke="#b8901c" strokeWidth={0.4} {...THROUGH}>
          <circle cx={39.5} cy={107.5} r={1.4} />
          <circle cx={60.5} cy={107.5} r={1.4} />
        </g>
      </>
    ),
  },
  suit: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <path d="M43 88.5 L50 107 L57 88.5 Z" {...trim(ctx)} />
        <Both d="M43 88.5 L50 107 L46 113 L36.5 92 Z" {...shirt(ctx, 0.84)} />
        <g fill={darken(ctx.c.shirt, 0.6)} {...THROUGH}>
          <circle cx={50} cy={115} r={1.1} />
          <circle cx={50} cy={122} r={1.1} />
        </g>
      </>
    ),
  },
  referee: {
    sleeves: 'short',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {clipped(
          ctx,
          'ref',
          <g {...trim(ctx)}>
            {[33, 40, 47, 54, 61, 68].map((x) => (
              <rect key={x} x={x - 1.7} y={86} width={3.4} height={50} />
            ))}
          </g>,
        )}
        {crew(ctx)}
      </>
    ),
  },
  coach: {
    sleeves: 'long',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <Both d="M42 87 L50 95 L44 100 L37 91 Z" {...shirt(ctx, 0.84)} />
        {trimLine(ctx, 'M50 95 L50 132', 1.4)}
        <rect x={55} y={103} width={7} height={5} rx={1} {...trim(ctx)} />
      </>
    ),
  },
  puffer: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        <path
          d="M34 89 Q50 85.5 66 89 Q71.5 90 71.5 96 L70 131 Q50 135 30 131 L28.5 96 Q28.5 90 34 89 Z"
          {...shirt(ctx)}
        />
        <path
          d="M30 103 Q50 107 70 103 M30 115 Q50 119 70 115"
          stroke={darken(ctx.c.shirt, 0.78)}
          strokeWidth={1}
          fill="none"
          {...THROUGH}
        />
        <rect x={40} y={83} width={20} height={10} rx={4.5} {...shirt(ctx, 0.92)} />
        {trimLine(ctx, 'M50 85 L50 132', 1.2)}
      </>
    ),
  },
  tracksuit: {
    sleeves: 'long',
    cuff: 'stripe',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <rect x={42} y={84} width={16} height={8} rx={3} {...shirt(ctx, 0.9)} />
        {trimLine(ctx, 'M50 86 L50 132', 1.2)}
      </>
    ),
  },
  dress: {
    sleeves: 'none',
    legs: 'skirt',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        <path d="M37 93 Q50 98 63 93 L65 123 L35 123 Z" {...shirt(ctx)} />
        <Both d="M38 94 L40 88.5" stroke={ctx.c.shirt} strokeWidth={1.8} data-slot="shirt" />
        {trimLine(ctx, 'M35.5 121 L64.5 121', 2.2)}
      </>
    ),
  },
  super: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {crew(ctx)}
        <path d={star(50, 108, 8, 3.5)} {...trim(ctx)} />
        {trimLine(ctx, 'M31.5 125 Q50 128 68.5 125', 3)}
      </>
    ),
  },
  robot: {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        <rect x={44} y={83} width={12} height={8} {...shirt(ctx, 0.8)} />
        <rect
          x={31}
          y={89}
          width={38}
          height={42}
          rx={4}
          {...shirt(ctx)}
          stroke={darken(ctx.c.shirt, 0.62)}
          strokeWidth={1.4}
        />
        <rect x={39} y={98} width={22} height={18} rx={2} {...shirt(ctx, 0.86)} />
        <circle cx={50} cy={107} r={3.4} {...trim(ctx)} />
        <g fill={darken(ctx.c.shirt, 0.6)} {...THROUGH}>
          <circle cx={42} cy={122} r={1.2} />
          <circle cx={47} cy={122} r={1.2} />
          <circle cx={53} cy={122} r={1.2} />
          <circle cx={58} cy={122} r={1.2} />
        </g>
      </>
    ),
  },
  sweater: {
    sleeves: 'long',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <path d="M42 88.5 Q50 93.5 58 88.5 L58 91.5 Q50 97 42 91.5 Z" {...shirt(ctx, 0.82)} />
        {trimLine(
          ctx,
          'M31 107 L35 103 L39 107 L43 103 L47 107 L51 103 L55 107 L59 103 L63 107 L67 103 L69 105',
          1.6,
        )}
        <g {...trim(ctx)}>
          {[36, 50, 64].map((x) => (
            <circle key={x} cx={x} cy={116} r={1.4} />
          ))}
        </g>
      </>
    ),
  },
  santa: {
    sleeves: 'long',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        <rect x={46.5} y={89} width={7} height={43} {...trim(ctx)} />
        <rect x={31} y={117} width={38} height={6} fill="#1d1d1d" {...THROUGH} />
        <rect
          x={45.5}
          y={116}
          width={9}
          height={8}
          rx={1}
          fill="none"
          stroke="#f2c230"
          strokeWidth={1.4}
          {...THROUGH}
        />
      </>
    ),
  },
  'pumpkin-suit': {
    sleeves: 'long',
    legs: 'pants',
    legsFrom: 'shirt',
    draw: (ctx) => (
      <>
        <ellipse cx={50} cy={112} rx={22} ry={21} {...shirt(ctx)} />
        <path
          d="M40 93 Q35 112 40 131 M50 91 L50 133 M60 93 Q65 112 60 131"
          stroke={darken(ctx.c.shirt, 0.8)}
          strokeWidth={1.2}
          fill="none"
          {...THROUGH}
        />
        <path d="M41 89 Q46 95 50 90 Q54 95 59 89 Q55 98 50 94 Q45 98 41 89 Z" {...trim(ctx)} />
      </>
    ),
  },
  flannel: {
    sleeves: 'long',
    cuff: 'band',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        {torso(ctx)}
        {clipped(
          ctx,
          'plaid',
          <g stroke={ctx.c.shirtTrim} data-slot="shirtTrim">
            <path d="M38 86 L38 134 M50 86 L50 134 M62 86 L62 134" strokeWidth={2.4} />
            <path d="M28 104 L72 104 M28 118 L72 118" strokeWidth={2} />
          </g>,
        )}
        <Both d="M42 87.5 L50 95 L43.5 99 Z" {...shirt(ctx, 0.85)} />
      </>
    ),
  },
  leprechaun: {
    sleeves: 'long',
    sleeve: '#f4f4f4',
    legs: 'pants',
    legsFrom: 'pants',
    draw: (ctx) => (
      <>
        <path d={TORSO} fill="#f4f4f4" {...THROUGH} />
        <Both
          d="M35 89.5 L44 89.5 L50 116 L50 133 Q41 133 32 131 L30.5 95 Q30.5 90.5 35 89.5 Z"
          {...shirt(ctx)}
        />
        <g {...trim(ctx)}>
          <circle cx={47} cy={118} r={1.4} />
          <circle cx={47} cy={125} r={1.4} />
        </g>
      </>
    ),
  },
};

// ── The number ─────────────────────────────────────────────────────────────

/** Block digits in a 6 × 10 cell, as strokes: a jersey's numerals, the same everywhere. */
const DIGITS: Record<string, string> = {
  '0': 'M1.5 0 L4.5 0 Q6 0 6 1.5 L6 8.5 Q6 10 4.5 10 L1.5 10 Q0 10 0 8.5 L0 1.5 Q0 0 1.5 0 Z',
  '1': 'M1.6 1.6 L3.6 0 L3.6 10',
  '2': 'M0.2 2.2 Q0.6 0 3 0 Q6 0 6 2.6 Q6 4.6 3 6.6 L0 10 L6 10',
  '3': 'M0.3 0 L5.7 0 L2.8 4 Q6 4 6 7 Q6 10 3 10 Q0.6 10 0 8.2',
  '4': 'M4.4 10 L4.4 0 L0 7 L6 7',
  '5': 'M5.8 0 L0.8 0 L0.3 4.6 Q1.6 3.9 3 3.9 Q6 3.9 6 7 Q6 10 3 10 Q0.6 10 0 8.4',
  '6': 'M5.4 0.4 Q1.2 0.6 0.3 5.6 Q0 10 3 10 Q6 10 6 7.1 Q6 4.3 3 4.3 Q1.2 4.3 0.3 5.6',
  '7': 'M0 0 L6 0 L2.2 10',
  '8': 'M3 4.6 Q0.5 4.6 0.5 2.3 Q0.5 0 3 0 Q5.5 0 5.5 2.3 Q5.5 4.6 3 4.6 Q0 4.6 0 7.3 Q0 10 3 10 Q6 10 6 7.3 Q6 4.6 3 4.6 Z',
  '9': 'M0.6 9.6 Q4.8 9.4 5.7 4.4 Q6 0 3 0 Q0 0 0 2.9 Q0 5.7 3 5.7 Q4.8 5.7 5.7 4.4',
};

/** The number on the chest, centered, in the trim, with a thin edge of the outfit's own shade. */
function NumberOn({ ctx, n, y }: { ctx: Ctx; n: string; y: number }) {
  const k = 1.35;
  const cell = 6 * k;
  const gap = 2.2;
  const width = n.length * cell + (n.length - 1) * gap;
  const x0 = 50 - width / 2;
  return (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      {[...n].map((d, i) => (
        <g key={i} transform={`translate(${x0 + i * (cell + gap)} ${y}) scale(${k})`}>
          <path d={DIGITS[d]} stroke={darken(ctx.c.shirt, 0.7)} strokeWidth={2.5} {...THROUGH} />
          <path d={DIGITS[d]} stroke={ctx.c.shirtTrim} strokeWidth={1.6} data-slot="shirtTrim" />
        </g>
      ))}
    </g>
  );
}

// ── The body ───────────────────────────────────────────────────────────────

function Legs({ ctx, look }: { ctx: Ctx; look: OutfitLook }) {
  const from: FaceArea = look.legsFrom === 'shirt' ? 'shirt' : 'pants';
  const skin = paint(ctx.c, 'skin', 0.94);
  const shoes = (
    <g {...paint(ctx.c, 'shoes')}>
      <ellipse cx={40.5} cy={152.5} rx={8.5} ry={4.2} />
      <ellipse cx={59.5} cy={152.5} rx={8.5} ry={4.2} />
    </g>
  );
  if (look.legs === 'skirt')
    return (
      <>
        <rect x={37} y={136} width={9} height={16} rx={3} {...skin} />
        <rect x={54} y={136} width={9} height={16} rx={3} {...skin} />
        {shoes}
        <path d="M34 120 L66 120 L74 143 Q50 147 26 143 Z" {...paint(ctx.c, from)} />
      </>
    );
  if (look.legs === 'shorts')
    return (
      <>
        <rect x={36} y={136} width={10} height={16} rx={3} {...skin} />
        <rect x={54} y={136} width={10} height={16} rx={3} {...skin} />
        {shoes}
        <path
          d="M32 126 L68 126 L68.5 142 L52 142 L50 134 L48 142 L31.5 142 Z"
          {...paint(ctx.c, from)}
        />
      </>
    );
  return (
    <>
      {shoes}
      <path
        d="M32 126 L68 126 L67.5 151 L52 151 L50 139 L48 151 L32.5 151 Z"
        {...paint(ctx.c, from)}
      />
    </>
  );
}

/** One arm: skin from shoulder to hand, its sleeve over as far as it goes, and the hand. */
function ArmDrawing({
  ctx,
  look,
  from,
  arm,
  hand = true,
}: {
  ctx: Ctx;
  look: OutfitLook;
  from: Point;
  arm: Arm;
  /** Off where a held thing is drawn between the arm and its hand. */
  hand?: boolean;
}) {
  const [sx, sy] = from;
  const [ex, ey] = arm.e;
  const [hx, hy] = arm.h;
  const whole = `M${sx} ${sy} L${ex} ${ey} L${hx} ${hy}`;
  const sleeve = look.sleeve ?? 'shirt';
  const area = sleeve.startsWith('#')
    ? { fill: sleeve, ...THROUGH }
    : paint(ctx.c, sleeve as FaceArea, 0.92);
  const color = area.fill;
  // Short sleeves stop part way down the upper arm.
  const mid: Point = [sx + (ex - sx) * 0.62, sy + (ey - sy) * 0.62];
  const sleevePath =
    look.sleeves === 'long'
      ? whole
      : look.sleeves === 'short'
        ? `M${sx} ${sy} L${mid[0]} ${mid[1]}`
        : null;
  // A CUFF IS A BAND ACROSS THE SLEEVE near its end: the last stretch of it
  // in the trim, its ends square, inside the sleeve's rounded end.
  const along = (a: Point, b: Point, t: number): Point => [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
  ];
  const cuff: readonly [Point, Point] =
    look.sleeves === 'long'
      ? [along(arm.e, arm.h, 0.66), along(arm.e, arm.h, 0.8)]
      : [along(from, arm.e, 0.5), along(from, arm.e, 0.6)];
  const slot = 'data-slot' in area ? area['data-slot'] : undefined;
  return (
    <g strokeLinecap="round" strokeLinejoin="round" fill="none">
      <path d={whole} stroke={ctx.c.skin} strokeWidth={7.5} data-slot="skin" />
      {sleevePath ? (
        <path d={sleevePath} stroke={color} strokeWidth={9.5} data-slot={slot} />
      ) : null}
      {look.cuff === 'band' && sleevePath ? (
        <path
          d={`M${cuff[0][0]} ${cuff[0][1]} L${cuff[1][0]} ${cuff[1][1]}`}
          stroke={ctx.c.shirtTrim}
          strokeWidth={9.5}
          strokeLinecap="butt"
          data-slot="shirtTrim"
        />
      ) : null}
      {look.cuff === 'stripe' && sleevePath ? (
        <path d={sleevePath} stroke={ctx.c.shirtTrim} strokeWidth={2} data-slot="shirtTrim" />
      ) : null}
      {hand ? <Hand ctx={ctx} at={arm.h} /> : null}
    </g>
  );
}

function Hand({ ctx, at }: { ctx: Ctx; at: Point }) {
  return (
    <circle
      cx={at[0]}
      cy={at[1]}
      r={4.8}
      {...paint(ctx.c, 'skin')}
      stroke={darken(ctx.c.skin, 0.8)}
      strokeWidth={0.6}
    />
  );
}

// ── What it holds ──────────────────────────────────────────────────────────

/** A held thing's own color, with an edge so it reads against a jersey or the stage of its color. */
const held = (ctx: Ctx, shade?: number) => ({
  ...paint(ctx.c, 'held', shade),
  stroke: darken(ctx.c.held, 0.62),
  strokeWidth: 0.5,
});
/** How much larger a held thing is drawn than its hand: big enough to read at a glance. */
const HELD_SCALE = 1.45;
const STRING = { stroke: '#666666', strokeWidth: 0.7, fill: 'none', ...THROUGH };

/** Each thing to hold, drawn about its hand, up being up; a parrot sits on the shoulder. */
const HELD: Record<Exclude<FaceHeld, 'none'>, (ctx: Ctx) => ReactNode> = {
  football: (ctx) => (
    <g transform="rotate(-35)">
      <ellipse cx={0} cy={-2} rx={8.5} ry={5.2} {...held(ctx)} />
      <path
        d="M-3 -2 L3 -2 M-2 -3.4 L-2 -0.6 M0 -3.4 L0 -0.6 M2 -3.4 L2 -0.6"
        stroke="#ffffff"
        strokeWidth={0.8}
        {...THROUGH}
      />
    </g>
  ),
  'foam-finger': (ctx) => (
    <g>
      <rect x={-7} y={-21} width={14} height={15} rx={4} {...held(ctx)} />
      <rect x={-2.7} y={-35} width={5.4} height={17} rx={2.7} {...held(ctx)} />
      <rect x={-7.5} y={-7.5} width={15} height={5} rx={1.5} {...held(ctx, 0.78)} />
    </g>
  ),
  pennant: (ctx) => (
    <g>
      <path d="M0 6 L0 -31" stroke="#8b5a2b" strokeWidth={1.6} {...THROUGH} />
      <path d="M0 -31 L27 -24 L0 -17 Z" {...held(ctx)} />
      <path d="M3 -28 L3 -20" stroke={darken(ctx.c.held, 0.7)} strokeWidth={1.4} {...THROUGH} />
    </g>
  ),
  'pom-poms': (ctx) => <PomPom ctx={ctx} />,
  megaphone: (ctx) => (
    <g>
      <path d="M-3 -5 L16 -15 L18 -1 L-1 1 Z" {...held(ctx)} />
      <ellipse cx={17} cy={-8} rx={2.6} ry={7.2} {...held(ctx, 0.7)} />
      <rect x={-2} y={-2} width={4} height={7} rx={1} fill="#444444" {...THROUGH} />
    </g>
  ),
  balloon: (ctx) => (
    <g>
      <path d="M0 0 Q-4 -16 3 -32" {...STRING} />
      <ellipse cx={3} cy={-43} rx={8.5} ry={10.5} {...held(ctx)} />
      <path d="M1.4 -32.2 L4.6 -32.2 L3 -33.8 Z" {...held(ctx, 0.75)} />
      <ellipse cx={0} cy={-47} rx={2} ry={3.2} fill="#ffffff" opacity={0.45} {...THROUGH} />
    </g>
  ),
  'heart-balloon': (ctx) => (
    <g>
      <path d="M0 0 Q-4 -16 3 -32" {...STRING} />
      <path d={heart(3, -42, 24)} {...held(ctx)} />
    </g>
  ),
  popcorn: (ctx) => (
    <g>
      <g fill="#fff4cc" stroke="#e8d79a" strokeWidth={0.5} {...THROUGH}>
        {[-5, -1.5, 2, 5.5, -3, 1, 4].map((x, i) => (
          <circle key={i} cx={x} cy={i < 4 ? -15 : -18} r={2.6} />
        ))}
      </g>
      <path d="M-7.5 -14 L7.5 -14 L5.5 4 L-5.5 4 Z" fill="#ffffff" {...THROUGH} />
      <path
        d="M-4.5 -14 L-3.5 4 M0 -14 L0 4 M4.5 -14 L3.5 4"
        stroke={ctx.c.held}
        strokeWidth={2.2}
        data-slot="held"
      />
    </g>
  ),
  soda: (ctx) => (
    <g>
      <path
        d="M3 -14 L6 -25"
        stroke="#ffffff"
        strokeWidth={1.6}
        strokeLinecap="round"
        {...THROUGH}
      />
      <path d="M-6.5 -13 L6.5 -13 L5 4 L-5 4 Z" {...held(ctx)} />
      <rect x={-7} y={-15.5} width={14} height={3} rx={1} fill="#ffffff" {...THROUGH} />
    </g>
  ),
  clipboard: (ctx) => (
    <g>
      <rect x={-8} y={-20} width={16} height={23} rx={1.5} {...held(ctx)} />
      <rect x={-6} y={-16.5} width={12} height={17} fill="#ffffff" {...THROUGH} />
      <path
        d="M-4 -12 L4 -12 M-4 -8 L4 -8 M-4 -4 L2 -4"
        stroke="#9aa3ad"
        strokeWidth={0.8}
        {...THROUGH}
      />
      <rect x={-3.5} y={-22} width={7} height={4} rx={1} fill="#c9ced6" {...THROUGH} />
    </g>
  ),
  teddy: (ctx) => (
    <g>
      <circle cx={0} cy={-6} r={7} {...held(ctx)} />
      <circle cx={-5} cy={-21} r={2.6} {...held(ctx)} />
      <circle cx={5} cy={-21} r={2.6} {...held(ctx)} />
      <circle cx={0} cy={-17} r={6} {...held(ctx)} />
      <ellipse cx={0} cy={-15.2} rx={2.6} ry={2} fill={lighten(ctx.c.held)} {...THROUGH} />
      <g fill={INK} {...THROUGH}>
        <circle cx={-2.2} cy={-18.4} r={0.8} />
        <circle cx={2.2} cy={-18.4} r={0.8} />
        <circle cx={0} cy={-15.6} r={0.8} />
      </g>
    </g>
  ),
  puppy: (ctx) => (
    <g>
      <ellipse cx={0} cy={-6} rx={7.5} ry={6} {...held(ctx)} />
      <ellipse cx={0} cy={-15} rx={7} ry={6.4} {...held(ctx)} />
      <Both
        d="M-5.5 -19 Q-11 -18 -9 -9 Q-6 -12 -5 -15 Z"
        transform="translate(0 0)"
        {...held(ctx, 0.72)}
      />
      <ellipse cx={0} cy={-12.5} rx={3.4} ry={2.4} fill={lighten(ctx.c.held)} {...THROUGH} />
      <g fill={INK} {...THROUGH}>
        <circle cx={-2.6} cy={-16.6} r={0.9} />
        <circle cx={2.6} cy={-16.6} r={0.9} />
        <ellipse cx={0} cy={-13.4} rx={1.3} ry={0.9} />
      </g>
    </g>
  ),
  flowers: (ctx) => (
    <g>
      <path
        d="M-4 -10 L-5 -18 M0 -10 L0 -21 M4 -10 L5 -18"
        stroke="#2e8b57"
        strokeWidth={1.2}
        {...THROUGH}
      />
      {(
        [
          [-5, -19],
          [0, -22],
          [5, -19],
        ] as const
      ).map(([x, y]) => (
        <g key={x}>
          <circle cx={x} cy={y} r={3.6} {...held(ctx)} />
          <circle cx={x} cy={y} r={1.3} fill="#ffd84d" {...THROUGH} />
        </g>
      ))}
      <path
        d="M-7 -11 L7 -11 L3 3 L-3 3 Z"
        fill="#f4ead2"
        stroke="#d8cba8"
        strokeWidth={0.5}
        {...THROUGH}
      />
    </g>
  ),
  mug: (ctx) => (
    <g>
      <path d="M-3 -17 Q-1 -20 -3 -23 M2 -17 Q4 -20 2 -23" {...STRING} />
      <rect
        x={-6.5}
        y={-14}
        width={11}
        height={13}
        rx={1.8}
        {...held(ctx)}
        stroke={darken(ctx.c.held, 0.7)}
        strokeWidth={0.6}
      />
      <path
        d="M4.5 -11 Q9.5 -11 9.5 -7.5 Q9.5 -4 4.5 -4"
        stroke={ctx.c.held}
        strokeWidth={1.8}
        fill="none"
        data-slot="held"
      />
    </g>
  ),
  parrot: (ctx) => (
    <g>
      <path d="M-2 6 L-6 18 L-1 16 L1 19 Z" {...held(ctx, 0.7)} />
      <ellipse cx={0} cy={0} rx={6} ry={9} transform="rotate(12)" {...held(ctx)} />
      <path d="M-5 -2 Q-8 6 -3 10 Q-2 3 -5 -2 Z" fill="#e53935" {...THROUGH} />
      <circle cx={2} cy={-9} r={5.2} {...held(ctx)} />
      <path d="M6.5 -10.5 Q10 -9 7.5 -5.5 L5.8 -7.6 Z" fill="#f2c230" {...THROUGH} />
      <circle cx={3.4} cy={-10.2} r={1} fill={INK} {...THROUGH} />
    </g>
  ),
  trophy: (ctx) => (
    // The champion's cup, held up.
    <g>
      <path d="M-7.5 -26 L7.5 -26 L6.5 -15 Q0 -8 -6.5 -15 Z" {...held(ctx)} />
      <path
        d="M-7.3 -24 Q-13 -23 -10.5 -17 L-6 -16 M7.3 -24 Q13 -23 10.5 -17 L6 -16"
        stroke={darken(ctx.c.held, 0.62)}
        strokeWidth={1.4}
        fill="none"
        {...THROUGH}
      />
      <rect x={-1.6} y={-11} width={3.2} height={6} {...held(ctx, 0.85)} />
      <rect x={-6} y={-5.5} width={12} height={4.5} rx={1} {...held(ctx, 0.78)} />
      <path d={star(0, -19.5, 3.4, 1.5)} fill="#ffffff" opacity={0.75} {...THROUGH} />
    </g>
  ),
  'game-ball': (ctx) => (
    // A football with a game ball's two white stripes and a gold star.
    <g transform="rotate(-35)">
      <ellipse cx={0} cy={-2} rx={8.5} ry={5.2} {...held(ctx)} />
      <path
        d="M-5.2 -6 Q-5.8 -2 -5.2 2 M5.2 -6 Q5.8 -2 5.2 2"
        stroke="#ffffff"
        strokeWidth={1.3}
        fill="none"
        {...THROUGH}
      />
      <path d="M-2.5 -2 L2.5 -2" stroke="#ffffff" strokeWidth={0.8} {...THROUGH} />
      <path d={star(0, -2, 2.4, 1)} fill="#f2c230" {...THROUGH} />
    </g>
  ),
  'candy-pail': (ctx) => (
    <g>
      <path d="M-7 11 Q0 -4 7 11" stroke="#333333" strokeWidth={1} fill="none" {...THROUGH} />
      <path d="M-8.5 10 L8.5 10 L7.5 25 Q0 27.5 -7.5 25 Z" {...held(ctx)} />
      <g fill={INK} {...THROUGH}>
        <path d="M-4.5 14 L-2 17 L-6 17 Z M4.5 14 L6 17 L2 17 Z" />
        <path d="M-4.5 20 L-2.5 22 L0 20.5 L2.5 22 L4.5 20 Q0 25 -4.5 20 Z" />
      </g>
    </g>
  ),
  pie: (ctx) => (
    <g>
      <ellipse cx={0} cy={-1} rx={11.5} ry={3.6} fill="#e0e0e0" {...THROUGH} />
      <path d="M-9.5 -2 Q0 -11 9.5 -2 Z" {...held(ctx)} />
      <path
        d="M-5 -2 L-2 -7.6 M0 -2 L2 -8.4 M5 -2 L5.4 -6"
        stroke={darken(ctx.c.held, 0.72)}
        strokeWidth={0.9}
        {...THROUGH}
      />
    </g>
  ),
  present: (ctx) => (
    <g>
      <rect x={-8.5} y={-17} width={17} height={15} rx={1} {...held(ctx)} />
      <path d="M0 -17 L0 -2 M-8.5 -10 L8.5 -10" stroke="#f2c230" strokeWidth={2.4} {...THROUGH} />
      <path
        d="M0 -17 Q-6 -24 -6 -18 Q-6 -16 0 -17 Q6 -24 6 -18 Q6 -16 0 -17 Z"
        fill="#f2c230"
        {...THROUGH}
      />
    </g>
  ),
  'pot-of-gold': (ctx) => (
    <g>
      <g fill="#f2c230" stroke="#b8901c" strokeWidth={0.5} {...THROUGH}>
        {[-6, -2, 2, 6, -4, 0, 4].map((x, i) => (
          <circle key={i} cx={x} cy={i < 4 ? -11 : -14} r={2.6} />
        ))}
      </g>
      <path d="M-9.5 -10 Q-11 4 0 4.5 Q11 4 9.5 -10 Z" {...held(ctx)} />
      <rect x={-10.5} y={-12} width={21} height={3} rx={1.5} {...held(ctx, 0.75)} />
    </g>
  ),
};

/** A cheerleader's pom-pom: a ball of strands in the held color. */
function PomPom({ ctx }: { ctx: Ctx }) {
  const rays = [0, 40, 80, 120, 160, 200, 240, 280, 320];
  return (
    <g>
      <circle cx={0} cy={-2} r={7.5} {...held(ctx)} />
      <g stroke={darken(ctx.c.held, 0.72)} strokeWidth={0.8} {...THROUGH}>
        {rays.map((a) => {
          const r = (a * Math.PI) / 180;
          return (
            <path
              key={a}
              d={`M${(Math.cos(r) * 2).toFixed(2)} ${(-2 + Math.sin(r) * 2).toFixed(2)} L${(Math.cos(r) * 7).toFixed(2)} ${(-2 + Math.sin(r) * 7).toFixed(2)}`}
            />
          );
        })}
      </g>
    </g>
  );
}

/** A color a little lighter: a teddy's muzzle on its fur. */
function lighten(hex: string, by = 0.35): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const up = (c: number) => Math.round(c + (255 - c) * by);
  const r = up((n >> 16) & 255);
  const g = up((n >> 8) & 255);
  const b = up(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/** A cape on the body: standing behind the head, falling behind to the knees (the neck's areas). */
function Cape({ ctx }: { ctx: Ctx }) {
  return (
    <g>
      <path d="M34 91 Q20 120 22 148 L78 148 Q80 120 66 91 Z" {...paint(ctx.c, 'neck')} />
      <path d="M38 92 Q28 120 30 146 L70 146 Q72 120 62 92 Z" {...paint(ctx.c, 'neckTrim')} />
      <Both d="M37 92 L24 70 Q24 84 30 94 Z" {...paint(ctx.c, 'neck')} />
    </g>
  );
}

// ── The character ──────────────────────────────────────────────────────────

export function FigureArt({
  face,
  mood = 'idle',
  team = NO_TEAM,
  stage = false,
  mirror = false,
  height,
  view,
  className,
}: {
  face: Face;
  mood?: FaceMood;
  /** The team's colors: an outfit nobody painted wears them, and the stage is them. */
  team?: FigureTeam;
  /** A rounded panel in the team's colors behind it, with a shadow under its feet. */
  stage?: boolean;
  /**
   * The pose the other way round, for the second of two shaking hands. Only
   * `shaking` turns: the other moods' motion and what they hold assume their
   * own way round.
   */
  mirror?: boolean;
  /** The drawn height; absent fills its box. */
  height?: number;
  /** Part of the box to show, as a `viewBox` (`FIGURE_PART_VIEW`). */
  view?: string;
  className?: string;
}) {
  const uid = useDrawingId();
  const ctx = faceCtx(face, team, uid);
  const outfit = faceOutfit(face);
  const look = OUTFITS[outfit.id];
  const turned = mirror && mood === 'shaking';
  const pose = turned ? mirrored(POSES[mood]) : POSES[mood];
  // WHAT IS IN HAND IS PUT DOWN TO SHAKE ON IT; a parrot on the shoulder stays.
  const putDown = mood === 'shaking' && face.held !== 'parrot';
  const holds = face.held !== undefined && face.held !== 'none' && !putDown ? face.held : null;
  // Pom-poms are one in each hand, except the hand at the chin.
  const both = holds === 'pom-poms' && mood !== 'thinking';
  const onShoulder = holds === 'parrot';
  const inHand = holds !== null && !onShoulder;
  const number = face.number !== undefined && NUMBERED_OUTFITS.has(outfit.id) ? face.number : null;
  // THE LEFT ARM IN A GROUP OF ITS OWN, with anything it holds, so a wave
  // swings the hand and what is in it together (kit.css).
  const leftHeld =
    both && holds !== null ? (
      <>
        <g transform={`translate(${pose.l.h[0]} ${pose.l.h[1]}) scale(${HELD_SCALE})`}>
          {HELD[holds](ctx)}
        </g>
        <Hand ctx={ctx} at={pose.l.h} />
      </>
    ) : null;
  const leftArm = (
    <g className="fsp-fig-arm-l">
      <ArmDrawing ctx={ctx} look={look} from={SHOULDER_L} arm={pose.l} hand={!both} />
      {pose.behind ? null : leftHeld}
    </g>
  );
  const rightArm = (
    <g className="fsp-fig-arm-r">
      <ArmDrawing ctx={ctx} look={look} from={SHOULDER_R} arm={pose.r} hand={!inHand} />
    </g>
  );
  const holding = inHand ? (
    <>
      <g transform={`translate(${pose.r.h[0]} ${pose.r.h[1]}) scale(${HELD_SCALE})`}>
        {HELD[holds](ctx)}
      </g>
      <Hand ctx={ctx} at={pose.r.h} />
      {pose.behind ? leftHeld : null}
    </>
  ) : null;
  const ratio = view
    ? Number(view.split(' ')[2]) / Number(view.split(' ')[3])
    : FIGURE_BOX.w / FIGURE_BOX.h;
  return (
    <svg
      className={`fsp-botface fsp-figure${className ? ` ${className}` : ''}`}
      data-state={mood}
      data-mirror={turned ? '' : undefined}
      viewBox={view ?? BOX}
      width={height === undefined ? undefined : Math.round(height * ratio)}
      height={height}
      aria-hidden
      focusable="false"
    >
      {stage ? (
        <>
          <rect
            x={FIGURE_BOX.x + 1}
            y={FIGURE_BOX.y + 1}
            width={FIGURE_BOX.w - 2}
            height={FIGURE_BOX.h - 2}
            rx={18}
            fill={team.secondary}
            data-slot="ring"
          />
          <rect
            x={FIGURE_BOX.x + 4}
            y={FIGURE_BOX.y + 4}
            width={FIGURE_BOX.w - 8}
            height={FIGURE_BOX.h - 8}
            rx={15}
            fill={team.primary}
            data-slot="disc"
          />
        </>
      ) : null}
      <ellipse cx={50} cy={154} rx={27} ry={4.2} fill="#000000" opacity={0.16} {...THROUGH} />
      <g className="fsp-fig-all">
        {face.neck === 'cape' ? <Cape ctx={ctx} /> : null}
        {look.behind?.(ctx)}
        {pose.behind ? (
          <>
            {leftArm}
            {rightArm}
          </>
        ) : null}
        <rect x={44} y={80} width={12} height={14} {...paint(ctx.c, 'skin', 0.88)} />
        <Legs ctx={ctx} look={look} />
        {look.draw(ctx)}
        {number ? <NumberOn ctx={ctx} n={number} y={outfit.id === 'tank' ? 106 : 104} /> : null}
        <FaceDrawing ctx={ctx} figure />
        {pose.behind ? null : (
          <>
            {leftArm}
            {rightArm}
          </>
        )}
        {holding}
        {onShoulder ? (
          <g transform={`translate(77 82) scale(${HELD_SCALE})`}>{HELD.parrot(ctx)}</g>
        ) : null}
      </g>
    </svg>
  );
}
